import React, { useEffect, useState } from 'react';
import { ApiError, apiFetch } from '../../lib/api';
import { formatMaxDisplay, formatRuleText, isParseError, parseFormatInput } from '../../lib/shooting';
import type { TournamentSummary } from '../../hooks/useScoringTournament';
import { FormatFields, type FormatDraft } from './FormatFields';

interface EventOption {
  id: string;
  title: string;
  date: number;
  importCount: number;
  registrations: number;
}

interface CreateTournamentCardProps {
  onCancel: () => void;
  onCreated: (result: { tournament: TournamentSummary; imported: number }) => void;
}

const field =
  'h-12 w-full rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] px-3 text-[var(--color-text-primary)]';

/** Name, format and the event whose registrations become the shooter list (imported once). */
export function CreateTournamentCard({ onCancel, onCreated }: CreateTournamentCardProps) {
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<FormatDraft>({ shots: '10', counted: '' });
  const [eventId, setEventId] = useState('');
  const [events, setEvents] = useState<EventOption[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ events: EventOption[] }>('/api/admin/shooting/events')
      .then((body) => {
        if (!cancelled) setEvents(body.events);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const chosen = events?.find((item) => item.id === eventId) ?? null;
  const format = parseFormatInput({ shotsPerShooter: draft.shots, countedShots: draft.counted });

  async function create(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) return setError('Enter the tournament name.');
    if (isParseError(format)) return setError(format.error);
    setBusy(true);
    setError(null);
    try {
      const body = await apiFetch<{ tournament: TournamentSummary; imported: number }>('/api/admin/shooting/tournaments', {
        method: 'POST',
        body: JSON.stringify({ name, ...format.format, eventId: eventId || null }),
      });
      onCreated(body);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the tournament.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(event) => void create(event)}
      className="space-y-4 rounded-[var(--radius-lg)] border border-[var(--color-accent)] p-4"
      aria-label="New tournament"
    >
      <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">New tournament</h2>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">Name</span>
        <input className={field} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="e.g. Air Pistol 2027" />
      </label>
      <FormatFields value={draft} onChange={setDraft} />
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">Import registrations from</span>
        <select className={field} value={eventId} onChange={(e) => setEventId(e.target.value)} aria-label="Import registrations from">
          <option value="">No event (add names by hand)</option>
          {(events ?? []).map((item) => (
            <option key={item.id} value={item.id}>
              {item.title} ({item.importCount} to import)
            </option>
          ))}
        </select>
      </label>
      <p className="text-sm text-[var(--color-text-secondary)]" role="status">
        {isParseError(format) ? `${format.error} ` : `${formatRuleText(format.format)} · max ${formatMaxDisplay(format.format)}. `}
        {chosen
          ? `Will import ${chosen.importCount} of ${chosen.registrations} registrations (payment verified or not needed), once.`
          : 'No shooters will be imported. Add them with the form below the list.'}
      </p>
      {error ? (
        <p className="text-sm font-medium text-[var(--color-error-text)]" role="alert">
          {error}
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={onCancel} className="min-h-12 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] text-sm font-medium">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !name.trim() || !format.ok}
          className="min-h-12 rounded-[var(--radius-md)] bg-[var(--color-accent)] text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Creating…' : chosen ? `Create and import ${chosen.importCount}` : 'Create'}
        </button>
      </div>
    </form>
  );
}
